import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { getVacancyLifecycleStatus } from './VacancyList';
import {
    countVacanciesForLifecycleStages,
    filterVacanciesInScope,
    type VacancyClientFilterScope,
    type VacancyLifecycleStage,
    VACANCY_LIFECYCLE_STAGE_OPTIONS,
    vacancyMatchesLifecycleStages,
} from '../utils/vacancyClientFilters';
import { searchTextIncludes } from '../utils/searchText';

function ScopeCheckboxRow({
    checked,
    onSelect,
    children,
}: {
    checked: boolean;
    onSelect: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={`flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-indigo-50 ${
                checked ? 'bg-indigo-50/90' : ''
            }`}
        >
            <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                    checked ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white'
                }`}
                aria-hidden
            >
                {checked ? <Check size={10} strokeWidth={3} /> : null}
            </span>
            <span className="min-w-0 flex-1 text-[12px] leading-snug">{children}</span>
        </button>
    );
}

export type GlobalVacancyLifecycleFilterProps = {
    vacancies: readonly any[];
    lifecycleStages: readonly VacancyLifecycleStage[];
    onLifecycleStagesChange: (stages: VacancyLifecycleStage[]) => void;
    selectedVacancyId: string;
    onSelectedVacancyIdChange: (vacancyId: string) => void;
    clientScope?: VacancyClientFilterScope;
    clientMap?: Record<string, { name?: string }>;
    applicationCountByVacancyId?: Record<string, number>;
    isClientPortal?: boolean;
    className?: string;
};

const GlobalVacancyLifecycleFilter: React.FC<GlobalVacancyLifecycleFilterProps> = ({
    vacancies,
    lifecycleStages,
    onLifecycleStagesChange,
    selectedVacancyId,
    onSelectedVacancyIdChange,
    clientScope = {},
    clientMap = {},
    applicationCountByVacancyId,
    isClientPortal = false,
    className = '',
}) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearchQuery('');
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    const scopedVacancies = useMemo(
        () =>
            filterVacanciesInScope(
                vacancies.filter((v) =>
                    vacancyMatchesLifecycleStages(v, lifecycleStages, {
                        excludePortalArchived: isClientPortal,
                    })
                ),
                clientScope
            ),
        [vacancies, lifecycleStages, clientScope, isClientPortal]
    );

    const stageCounts = useMemo(() => {
        const base = filterVacanciesInScope(vacancies, clientScope);
        const m = new Map<VacancyLifecycleStage, number>();
        for (const opt of VACANCY_LIFECYCLE_STAGE_OPTIONS) {
            m.set(
                opt.id,
                base.filter((v) =>
                    vacancyMatchesLifecycleStages(v, [opt.id], { excludePortalArchived: isClientPortal })
                ).length
            );
        }
        return m;
    }, [vacancies, clientScope, isClientPortal]);

    const displayValue = useMemo(() => {
        if (selectedVacancyId) {
            const v = vacancies.find((x) => x.id === selectedVacancyId);
            const num = v?.vacancy_number ? `#${v.vacancy_number} ` : '';
            const title = `${num}${v?.title || 'Vacature'}`.trim();
            const n = applicationCountByVacancyId?.[selectedVacancyId];
            return n != null && n > 0 ? `${title} (${n})` : title;
        }
        const labels = VACANCY_LIFECYCLE_STAGE_OPTIONS.filter((o) =>
            lifecycleStages.includes(o.id)
        ).map((o) => o.label);
        const stageLabel = labels.length ? labels.join(' + ') : 'Geen status';
        const n = countVacanciesForLifecycleStages(vacancies, lifecycleStages, {
            excludePortalArchived: isClientPortal,
        });
        return `${stageLabel} (${n} vacatures)`;
    }, [
        selectedVacancyId,
        vacancies,
        lifecycleStages,
        applicationCountByVacancyId,
        isClientPortal,
    ]);

    const toggleStage = (stage: VacancyLifecycleStage) => {
        onSelectedVacancyIdChange('');
        const has = lifecycleStages.includes(stage);
        const next = has
            ? lifecycleStages.filter((s) => s !== stage)
            : [...lifecycleStages, stage];
        onLifecycleStagesChange(next);
    };

    const filteredVacs = scopedVacancies.filter(
        (v) =>
            !searchQuery ||
            searchTextIncludes(v.title, searchQuery) ||
            searchTextIncludes(String(v.vacancy_number ?? ''), searchQuery)
    );

    const openVacancies = filteredVacs.filter(
        (v) => getVacancyLifecycleStatus(v).toLowerCase() === 'open'
    );
    const doingVacancies = filteredVacs.filter(
        (v) => getVacancyLifecycleStatus(v).toLowerCase() === 'doing'
    );
    const closedVacancies = filteredVacs.filter(
        (v) => getVacancyLifecycleStatus(v).toLowerCase() === 'gesloten'
    );

    const renderVacancyRow = (v: any) => {
        const checked = selectedVacancyId === v.id;
        const ogId = v.opdrachtgever_id || v.opdrachtgever?.id;
        const vMetaOp =
            (ogId && clientMap[ogId]?.name) ||
            v.opdrachtgever?.name ||
            (v.client_id && clientMap[v.client_id]?.name);
        const appN = applicationCountByVacancyId?.[v.id] ?? 0;
        return (
            <ScopeCheckboxRow
                key={v.id}
                checked={checked}
                onSelect={() => {
                    onSelectedVacancyIdChange(checked ? '' : v.id);
                    setOpen(false);
                    setSearchQuery('');
                }}
            >
                <span className="font-medium text-slate-800 truncate block" title={v.title}>
                    {v.vacancy_number ? (
                        <span className="font-mono text-indigo-600 mr-1">#{v.vacancy_number}</span>
                    ) : null}
                    {v.title}
                    {appN > 0 ? (
                        <span className="ml-1 font-semibold text-slate-500">({appN})</span>
                    ) : null}
                </span>
                <span className="text-[10px] text-slate-500 font-medium truncate block mt-0.5">
                    {vMetaOp || v.data?.werkplek || v.data?.clientName || 'Onbekende werkplek'}
                </span>
            </ScopeCheckboxRow>
        );
    };

    return (
        <div ref={rootRef} className={`relative z-50 min-w-0 w-full ${className}`}>
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
                className="relative flex w-full items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-1.5 pr-8 text-left text-[12px] font-bold text-slate-700 shadow-sm outline-none transition-colors hover:border-slate-300 focus:border-indigo-500"
            >
                <span className="min-w-0 flex-1 truncate">{displayValue}</span>
                <ChevronDown
                    size={14}
                    className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 transition-transform ${
                        open ? 'rotate-180' : ''
                    }`}
                />
            </button>
            {open && (
                <div
                    className="absolute top-full left-0 right-0 z-50 mt-1 max-h-96 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <div className="border-b border-slate-100 bg-slate-50/80 px-3 py-2">
                        <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                            Vacaturestatus
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {VACANCY_LIFECYCLE_STAGE_OPTIONS.map((opt) => {
                                const checked = lifecycleStages.includes(opt.id);
                                const n = stageCounts.get(opt.id) ?? 0;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => toggleStage(opt.id)}
                                        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                                            checked
                                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                                : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300'
                                        }`}
                                    >
                                        <span
                                            className={`flex h-3.5 w-3.5 items-center justify-center rounded border ${
                                                checked
                                                    ? 'border-white/80 bg-white/20'
                                                    : 'border-slate-300 bg-white'
                                            }`}
                                        >
                                            {checked ? <Check size={8} strokeWidth={3} /> : null}
                                        </span>
                                        {opt.label} ({n})
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="sticky top-0 z-20 border-b border-slate-100 bg-white p-2">
                        <div className="relative">
                            <Search
                                size={12}
                                className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Zoek vacature…"
                                className="w-full rounded-lg border border-slate-200 py-1.5 pl-7 pr-2 text-[11px] outline-none focus:border-indigo-400"
                            />
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            onSelectedVacancyIdChange('');
                            setOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-[11px] hover:bg-slate-50 ${
                            !selectedVacancyId
                                ? 'bg-indigo-50/50 font-semibold text-indigo-700'
                                : 'text-slate-500'
                        }`}
                    >
                        Alle vacatures in gekozen status
                    </button>

                    {openVacancies.length > 0 && (
                        <>
                            <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Open ({openVacancies.length})
                            </div>
                            {openVacancies.map(renderVacancyRow)}
                        </>
                    )}
                    {doingVacancies.length > 0 && (
                        <>
                            <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Doing ({doingVacancies.length})
                            </div>
                            {doingVacancies.map(renderVacancyRow)}
                        </>
                    )}
                    {closedVacancies.length > 0 && (
                        <>
                            <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Gesloten ({closedVacancies.length})
                            </div>
                            {closedVacancies.map(renderVacancyRow)}
                        </>
                    )}

                    {filteredVacs.length === 0 && lifecycleStages.length === 0 && (
                        <p className="px-3 py-4 text-center text-[11px] text-slate-400">
                            Selecteer minimaal één status (Open, Doing of Gesloten).
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default GlobalVacancyLifecycleFilter;
